import { useEffect, useRef } from 'react';

import { pubSub } from 'utils/pubSub';

import {
  CommandNameInternal,
  CommandPubSubEvent,
} from '../types/command.types';
import { commandPubSubEventNamePrefix } from '../utils/commandUtils';

type UseCommandHandleOptions = {
  /** The name of the command. */
  readonly name: CommandNameInternal;
  /** The action function that the command performs. */
  readonly actionFn: () => void;
};

/** A hook for assigning a handler to a command. */
export function useCommandHandler(options: UseCommandHandleOptions): void {
  const { name, actionFn } = options;
  const pubSubEventName =
    `${commandPubSubEventNamePrefix}${name}` as const satisfies keyof CommandPubSubEvent;
  const pubSubHandler = useRef<typeof actionFn>(actionFn);

  // Handles updating the current pubSub handler.
  useEffect(() => {
    pubSubHandler.current = actionFn;
  }, [actionFn]);

  // Handles subscribing to the pubSub event and executing the command.
  useEffect(() => {
    const unsubscribe = pubSub.subscribe(pubSubEventName, () => {
      pubSubHandler.current();
    });

    return () => {
      unsubscribe();
    };
  }, [pubSubEventName]);
}
